import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "tek_cookie_consent";

const COPY = {
  et: {
    title: "Sait kasutab küpsiseid",
    body:
      "Kasutame küpsiseid kasutuskogemuse parandamiseks ja anonüümseks analüütikaks. Vajalikud küpsised on alati lubatud. Statistikaküpsised on lubatud ainult sinu nõusolekul.",
    accept: "Nõustun",
    reject: "Ainult vajalikud",
    learnMore: "Loe lähemalt küpsiste kohta",
  },
  ru: {
    title: "Сайт использует cookie",
    body:
      "Мы используем файлы cookie для улучшения работы сайта и анонимной аналитики. Необходимые cookie всегда включены. Статистические — только с вашего согласия.",
    accept: "Принять",
    reject: "Только необходимые",
    learnMore: "Подробнее о cookie",
  },
  en: {
    title: "This site uses cookies",
    body:
      "We use cookies to improve your experience and for anonymous analytics. Essential cookies are always on. Statistics cookies are loaded only with your consent.",
    accept: "Accept",
    reject: "Only essential",
    learnMore: "Learn more about cookies",
  },
};

export default function CookieBanner({ lang = "et" }) {
  const [visible, setVisible] = useState(false);
  const t = COPY[lang] || COPY.et;

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Delay slightly so it doesn't blink on first paint
        const id = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(id);
      }
    } catch (_e) {
      // localStorage may be unavailable; default to visible
      setVisible(true);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice: value, ts: new Date().toISOString() }),
      );
    } catch (_e) {
      // ignore storage failures
    }
    setVisible(false);
  };

  // Tell the AI assistant to hide its FAB while the consent card is shown —
  // both live in the bottom-right corner and the FAB covered the GDPR text.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("cookie-banner", { detail: { visible } }));
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t.title}
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60]"
    >
      <div className="rounded-2xl bg-surface-ink/95 text-white backdrop-blur-md shadow-ambientLg border border-white/10 p-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-teal-500/20 flex items-center justify-center shrink-0">
            <Cookie className="h-5 w-5 text-teal-300" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-[15px] font-semibold leading-tight">{t.title}</h3>
              <button
                aria-label="Close"
                onClick={() => persist("rejected")}
                className="text-white/60 hover:text-white shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-[13px] leading-relaxed text-white/80 mt-2">{t.body}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                data-testid="cookie-accept"
                onClick={() => persist("accepted")}
                className="rounded-lg bg-teal-500 hover:bg-teal-400 text-surface-ink px-4 py-2 text-[13px] font-semibold transition-colors"
              >
                {t.accept}
              </button>
              <button
                data-testid="cookie-reject"
                onClick={() => persist("rejected")}
                className="rounded-lg border border-white/20 hover:bg-white/10 px-4 py-2 text-[13px] font-medium transition-colors"
              >
                {t.reject}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
